import { Box, Stack, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  Maybe,
  MonthlyEventsByArtistFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import {
  EventScheduledCard,
  EventScheduledCardSkeleton,
} from '@/components/organisms'
import EventCalendar from '@/components/organisms/event/EventCalendar'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { buildEventDateArray } from '@/lib/array'

export * from './fragments'

const EVENT_CARD_CLASS_NAME = 'eventCard'

export type State = {
  loading: boolean
  month: string
  user: Maybe<SideMenuProps['user']>
  liveStreamId: string | undefined
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  isArtist: Maybe<SideMenuProps['isArtist']>
  monthlyEventsByArtist: Maybe<Array<MonthlyEventsByArtistFragment>>
}

export type Action = {
  onFetch: (month: string) => Promise<void>
}

export const ArtistEventsTemplate: Template<State, Action> = ({
  state,
  onFetch,
}) => {
  const { t } = useTranslation('artistEvents')
  const { routerNames } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  const scheduledEventList = useMemo(() => {
    const monthlyEvents = state?.monthlyEventsByArtist
    if (!monthlyEvents?.length) return []
    const uniqueSortedDates = [
      ...new Set(
        monthlyEvents.flatMap(event => buildEventDateArray(event, state.month))
      ),
    ]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .filter(date => {
        const [year, month] = date?.split('/') ?? []
        return `${year}${month}` === state.month
      })
    return uniqueSortedDates.map(date => ({
      date: date || '',
      events: monthlyEvents
        .filter(event =>
          buildEventDateArray(event, state.month)?.includes(date || '')
        )
        .sort((a, b) => a.startedAt - b.startedAt),
    }))
  }, [state?.monthlyEventsByArtist, state.month])

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStreamId,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
        margin
        header={{
          title: t('title'),
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              badge={
                state.user?.hasManagementTask ||
                !!state.user?.unreadNotificationCount
              }
              color="icon.line"
              bgcolor="icon.background"
            />
          ),
        }}
      >
        <Head title={routerNames.ArtistEvents} />
        <EventCalendar
          dates={scheduledEventList.map(({ date }) => date) ?? []}
          onFetch={onFetch}
          month={state.month}
          className={EVENT_CARD_CLASS_NAME}
        />
        {state.loading ? (
          <>
            <EventScheduledCardSkeleton />
            <EventScheduledCardSkeleton />
            <EventScheduledCardSkeleton />
          </>
        ) : scheduledEventList?.length ? (
          <Stack spacing={2} pt={4} sx={{ pb: 1 }}>
            {scheduledEventList.map((scheduledEvent, index) => (
              <EventScheduledCard
                scheduledEvent={scheduledEvent}
                key={scheduledEvent.date}
                month={state.month}
                className={EVENT_CARD_CLASS_NAME}
              />
            ))}
            <Box height={window.innerHeight - 220} />
          </Stack>
        ) : (
          <Stack height="76px" justifyContent="center" alignItems="center">
            <Typography color="text.secondary">{t('eventEmpty')}</Typography>
          </Stack>
        )}

        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}
